import React from 'react'
import PropTypes from 'prop-types'
import {
  first, cloneDeep, isArray, isEmpty, isFunction,
  isNil, has, map, omit, reduce, range, set, unset,
  isUndefined, startCase, compact,
  filter, includes, trim, forEach, size, isEqual
} from 'lodash'
import { Navigate as Redirect } from 'react-router'
import { AgGridReact } from 'ag-grid-react'
import agGridTheme from '../../lib/agGridTheme'
import ddRum from '../../lib/datadogRum'
import Component from '../common/Component'
import DiffCellRenderer from './DiffCellRenderer'
import SbcCellRenderer from './SbcCellRenderer'
import SobCellRenderer from './SobCellRenderer'
import BulkEditsErrorsComponent from './BulkEditsErrorsComponent'
import GridSelectOptionsHelper from './Helpers/GridSelectOptionsHelper'
import DocumentSelectField from '../common/DocumentSelectField'
import benefitValueFormatter from './Helpers/benefitValueFormatter'
import { networkTierType } from './Helpers/tierTypeFactory'

import '../../css/AgGrid.scss'
import '../../css/BulkEdits.scss'

// A checkbox field is boolean by definition (type IS the declaration), even
// when it declares no {text, value} options of its own — e.g. the 2 fields
// (quoted_via_carrier_api in HospitalIndemnity/CriticalIllness) with no
// `options`. Used as the display/coercion fallback for those so paste and
// display don't diverge from the boolean the column actually stores.
const DEFAULT_CHECKBOX_OPTIONS = [{ text: 'Yes', value: true }, { text: 'No', value: false }]

export default class BulkEditsComponent extends Component {
  static propTypes = {
    records: PropTypes.array.isRequired,
    defaultFields: PropTypes.object.isRequired,
    metadataFields: PropTypes.object.isRequired,
    benefitFields: PropTypes.object.isRequired,
    benefitFieldsWithoutCoverageTier: PropTypes.object,
    configuration: PropTypes.object.isRequired,
    createBulkUpdate: PropTypes.func.isRequired,
    bulkUpdate: PropTypes.object,
    higherPrioritySourceValidation: PropTypes.func.isRequired,
    resetHigherPrioritySourceValidation: PropTypes.func.isRequired,
    redirectUrl: PropTypes.string.isRequired,
    includeSbcCheckbox: PropTypes.bool,
    sbcCheckboxLabel: PropTypes.string,
    withSbc: PropTypes.bool,
    productLine: PropTypes.string,
    hideId: PropTypes.bool,
    hideSobUrl: PropTypes.bool,
    fieldGroups: PropTypes.object
  }

  static CACHE_KEY = 'bulk_edits_cache'

  constructor(props) {
    super(props)

    const records = this.shallowRecords(props.records)
    const changes = props.records.reduce((memo, value) => {
      return Object.assign(memo, { [value.id]: {} })
    }, {})

    const initialRecords = records.reduce((memo, value) => {
      return Object.assign(memo, { [value.id]: value })
    }, {})

    this.lastChanges = []
    this.isSaving = false

    this.state = {
      withSbc: props.withSbc || false,
      savePromise: new Promise((resolve, reject) => { resolve() }),
      records: cloneDeep(records),
      pendingUpdates: false,
      networkOnlyUpdates: false,
      initialRecords,
      changes
    }

    this.state.columns = this.columns
  }

  componentDidMount() {
    this.saveListener = document.addEventListener('keydown', (e) => {
      if (e.keyCode === 83 && (e.metaKey || e.ctrlKey)) {
        this.handleSave(e)
        e.preventDefault()
      }
      return true
    })
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.saveListener)
    this.props.resetHigherPrioritySourceValidation()
  }

  onGridReady = (params) => {
    this.gridApi = params.api
    window.__agGridApi = params.api

    this.autoSizeAll()
  }

  get isProductLineDental() {
    return this.props.productLine === 'dental'
  }

  get filteredRecords() {
    return filter(this.state.records, (record) => {
      if (this.state.withSbc) { return !isEmpty(record.sbc_url) }
      if (this.state.withSob) { return !isEmpty(record.sob_url) }

      return true
    })
  }

  get maxTiers() {
    if (isNil(this.props.records)) {
      return 0
    }

    const rowTiers = this.props.records.map((row) => {
      const benefitTiers = Object.values(row.benefits).map((benefit) => {
        const firstNilTier = range(2, 11).find((i) => isNil(benefit[`in_network_tier_${i}`]))

        if (isNil(firstNilTier) || firstNilTier === 2) {
          return 0
        }

        return firstNilTier - 1
      })

      return Math.max(...benefitTiers)
    })

    return Math.max(...rowTiers)
  }

  get rowCount() {
    return this.props.records.length
  }

  get metadatumColumns() {
    return map(this.props.metadataFields, (field, key) => {
      let editor
      let filterType
      let editorParams = {}
      let valueFormatter
      let valueParser
      const options = cloneDeep(field.options)

      switch (field.type) {
      case 'select':
        filterType = 'agSetColumnFilter'
        editor = 'agRichSelectCellEditor'

        if (isArray(options) && !isEmpty(options) && !has(first(options), 'text')) {
          options.push(null)
          editorParams = { values: options }
        } else if (isFunction(field.options)) {
          editorParams = (params) => {
            return { values: field.options(params.data, this.props.configuration).concat(null) }
          }
        } else {
          let values = this.props.configuration[key] || []

          if (!values.length && has(first(options), 'text')) {
            values = options
          }

          if (has(first(values), 'text')) {
            const optionsHelper = new GridSelectOptionsHelper(values)
            values = optionsHelper.values
            valueFormatter = (params) => {
              return optionsHelper.valueToText(params.value)
            }
            valueParser = (params) => {
              return optionsHelper.textToValue(params.value)
            }
          }

          editorParams = { values }
        }

        break
      case 'checkbox': {
        filterType = 'agSetColumnFilter'
        editor = 'agRichSelectCellEditor'
        editorParams = { values: [true, false] }

        const checkboxOptionsHelper = new GridSelectOptionsHelper(
          (isArray(options) && !isEmpty(options)) ? options : DEFAULT_CHECKBOX_OPTIONS
        )
        valueFormatter = (params) => {
          return checkboxOptionsHelper.valueToText(params.value)
        }
        editorParams.cellRenderer = (params) => {
          return checkboxOptionsHelper.valueToText(params.value)
        }

        break
      }
      case 'jsonTable':
        filterType = 'agTextColumnFilter'
        editor = 'agTextCellEditor'

        valueFormatter = (params) => {
          return params.value ? JSON.stringify(params.value, null, 2).replace(/\\/g, '') : ''
        }
        valueParser = (params) => {
          if (typeof params.newValue === 'string') {
            return JSON.parse(params.newValue.replace(/\\/g, ''))
          }
          return params.newValue
        }
        editorParams = {
          useFormatter: true
        }
        break
      default:
        filterType = 'agTextColumnFilter'
        editor = 'agTextCellEditor'
        valueParser = (params) => {
          return field.valueParser
            ? field.valueParser(params.newValue)
            : trim(params.newValue)
        }
      }

      const column = this.readOnlyFields.find((readOnlyField) => {
        return (field.bulkEditKey || key) === readOnlyField.field
      })

      if (column) {
        const invisible = this.excludedByPreset(field.bulkEditKey || key)

        return {
          ...column,
          hide: invisible,
          lockVisible: !invisible,
          cellEditor: editor,
          cellType: 'metadatum',
          filter: filterType,
          cellEditorParams: editorParams,
          valueFormatter,
          valueParser
        }
      }

      return {
        field: field.bulkEditKey || key,
        headerName: field.label,
        hide: field.hide || this.excludedByPreset(field.bulkEditKey || key),
        editable: !field.display_only,
        cellRenderer: DiffCellRenderer,
        cellClass: 'ag-grid-cell-diff',
        cellEditor: editor,
        cellType: 'metadatum',
        filter: filterType,
        cellEditorParams: editorParams,
        valueFormatter,
        valueParser,
        pinned: this.pinned(field.bulkEditKey || key)
      }
    })
  }

  get benefitColumns() {
    return map(this.props.benefitFields, (value, key) => {
      return {
        headerName: value,
        children: this.allBenefitFields(value).map((field) => ({
          field: `${key}_${field.key}`,
          headerName: field.label,
          hide: this.excludedByPreset(`${key}_${field.key}`),
          editable: true,
          filter: 'agTextColumnFilter',
          cellRenderer: DiffCellRenderer,
          cellClass: 'ag-grid-cell-diff',
          cellType: 'benefits',
          valueParser: (params) => {
            return benefitValueFormatter(params.newValue)
          }
        })),
        field: key
      }
    })
  }

  get benefitFieldOrderWithoutCoverageTier() {
    return this.unpinnedFieldOrder.map((field) => {
      return Object.keys(this.props.benefitFields).find((fieldType) => {
        const value = this.props.benefitFields[fieldType]
        const fields = this.allBenefitFields(value).map((benefit) => (`${fieldType}_${benefit.key}`))

        return fields.includes(field)
      })
    }).filter((str) => str)
  }

  get documentLookup() {
    if (this.state.withSbc || this.state.withSob) return ''
    return (
      <DocumentSelectField
        key="document"
        name="document"
        handleChange={this.handleChange}
        value={this.state.document}
        filterFields={this.remoteFilterQueryParams}
      />
    )
  }

  get sbcCheckbox() {
    if (!this.props.includeSbcCheckbox || this.state.withSob) { return null }

    return (
      <div>
        <input id="with_sbc" type="checkbox" onClick={this.filterWithSBC} checked={this.state.withSbc} />
        <label htmlFor="with_sbc">{this.props.sbcCheckboxLabel || 'Build From SBCs'}</label>
      </div>
    )
  }

  get sobCheckbox() {
    if (!this.props.includeSbcCheckbox || this.props.hideSobUrl || this.state.withSbc) { return null }

    return (
      <div>
        <input id="with_sob" type="checkbox" onClick={this.filterWithSOB} checked={this.state.withSob} />
        <label htmlFor="with_sob">Build From SOBs</label>
      </div>
    )
  }

  get presetDropdown() {
    const presets = Object.keys(this.props.defaultFields).filter((field) => { return field !== 'default' })

    if (presets.length === 0) { return null }

    const options = presets.map((field) => {
      if (this.props.defaultFields.field === this.props.defaultFields.default) {
        return <option key={field} value={field} selected="selected">{this.titleCase(field)}</option>
      }

      return <option key={field} value={field}>{this.titleCase(field)}</option>
    })

    return (
      <div>
        <label htmlFor="preset-dropdown">Filter Preset</label>
        <select name="preset-dropdown" id="preset-dropdown" onChange={this.applyPresetFilter}>{options}</select>
      </div>
    )
  }

  get readOnlyFields() {
    return [
      {
        field: 'id',
        headerName: 'ID',
        headerCheckboxSelection: false,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: false,
        lockPosition: this.pinned('id'),
        pinned: this.pinned('id'),
        lockVisible: !(this.props.hideId || !this.pinned('id')),
        hide: this.props.hideId || !this.pinned('id')
      },
      {
        field: 'sbc_url',
        cellRenderer: SbcCellRenderer,
        headerName: 'SBC URL',
        headerCheckboxSelection: false,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: false,
        lockPosition: this.pinned('sbc_url'),
        lockVisible: this.pinned('sbc_url'),
        pinned: this.pinned('sbc_url'),
        hide: !this.pinned('sbc_url')
      },
      {
        field: 'sob_url',
        cellRenderer: SobCellRenderer,
        headerName: 'SOB URL',
        headerCheckboxSelection: false,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: false,
        lockPosition: this.pinned('sob_url'),
        lockVisible: !(this.props.hideSobUrl || !this.pinned('sob_url')),
        pinned: this.pinned('sob_url'),
        hide: this.props.hideSobUrl || !this.pinned('sob_url')
      },
      {
        field: 'plan_ancestor_network',
        headerName: 'Plan Ancestor Network',
        display_on_cvt: false,
        hide: !this.pinned('plan_ancestor_network'),
        lockPosition: this.pinned('plan_ancestor_network'),
        pinned: this.pinned('plan_ancestor_network')
      },
      {
        field: 'hios_ids',
        filter: 'agTextColumnFilter',
        headerName: 'HIOS IDs',
        lockPosition: this.pinned('hios_ids'),
        pinned: this.pinned('hios_ids'),
        hide: !this.pinned('hios_ids')
      }
    ]
  }

  get sortedReadOnlyFields() {
    return this.sortedFields(
      this.readOnlyFields,
      this.props.defaultFields[this.presetFilter || 'default'].pinned
    )
  }

  get unpinnedFieldOrder() {
    return this.props.defaultFields[this.presetFilter || 'default'].values
  }

  get columns() {
    return this.sortedReadOnlyFields.concat([
      {
        headerName: 'Info',
        children: this.sortedFields(this.metadatumColumns, this.unpinnedFieldOrder)
      }])
      .concat(this.sortedFields(this.benefitColumns, this.benefitFieldOrderWithoutCoverageTier))
  }

  get rowData() {
    if (isNil(this.props.records)) {
      return []
    }

    return this.props.records.map(this.rowMapper)
  }

  get changes() {
    return this.state.records.map((record, index) => {
      return this.changesForRow(record, this.state.initialRecords[index])
    })
  }

  get updateValues() {
    let changes = this.applyLastChanges()
    const validIds = map(this.filteredRecords, (record) => { return record.id })

    changes = Object.keys(changes).map((id) => {
      const benefitsAttributes = this.updateBenefitValues(changes[id].benefits)
      const metadatumAttributes = omit(changes[id], ['benefits'])

      if (!includes(validIds, parseInt(id, 10)) || (isEmpty(benefitsAttributes) && isEmpty(metadatumAttributes))) {
        return null
      }

      const values = {
        benefits_set_id: parseInt(id, 10),
        benefits_attributes: benefitsAttributes,
        metadatum_attributes: metadatumAttributes,
        document_id: this.state.document
      }
      if (this.props.productLine === 'medical') {
        const filteredRecord = first(this.props.records.filter((record) => record.id === parseInt(id, 10)))
        values.plan_year = filteredRecord.plan_year
        values.hios_id = first(filteredRecord.hios_ids)
      }
      return values
    })

    return compact(changes)
  }

  get savingDisabled() {
    if (!this.state.pendingUpdates) {
      return true
    }
    if (this.state.networkOnlyUpdates) {
      return false
    }

    return this.noDocuments
  }

  get noDocuments() {
    const NoDocument = isNil(this.state.document)
    const NoSbc = !this.state.withSbc
    const NoSob = !this.state.withSob

    return NoDocument && NoSbc && NoSob
  }

  get remoteFilterQueryParams() {
    const documentProductLine = ['level_funded', 'large_group_custom'].includes(this.props.productLine)
      ? 'medical' : this.props.productLine

    return {
      product_line: startCase(documentProductLine),
      document_source_contents: ['Benefits', 'Metadata']
    }
  }

  get hasPendingChanges() {
    return this.state.pendingUpdates
  }

  get savingMsg() {
    if (this.noDocumens) {
      return 'Cannot save yet - select a Document, SBC or SOB'
    } if (this.props.bulkUpdate.requestInProgress) {
      return 'Saving...'
    } if (this.props.bulkUpdate.resourceErrors) {
      return 'Error Saving'
    } if (this.hasPendingChanges) {
      return 'Unsaved Changes'
    }
    return 'Saved'
  }

  get savingMsgClass() {
    if (this.props.bulkUpdate.resourceErrors || this.hasPendingChanges) {
      return 'alert'
    }
    return 'success'
  }

  get pendingUpdatesMessage() {
    return `Are you sure?  Leaving will lose changes for ${size(this.updateValues)} unsaved benefits set(s).`
  }

  allBenefitFields = (value) => {
    const tierType = this.tierTypeFromLabel(value)

    return tierType.benefitsFields(
      (this.isProductLineDental && !Object.values(this.props.benefitFieldsWithoutCoverageTier).includes(value)),
      this.maxTiers
    )
  }

  translateTiersAttributes = (benefit) => {
    const tiers = Object.keys(benefit).filter((key) => {
      return key !== 'limit' && key !== 'coverage_tier'
    })

    return tiers.map((tier) => {
      return {
        option: startCase(tier).replace('Of', 'of'),
        value: benefit[tier]
      }
    })
  }

  translateLimit = (benefit) => {
    const limit = benefit.limit

    if (isUndefined(limit)) {
      return null
    }

    return limit
  }

  translateCoverageTier = (benefit) => {
    const coverageTier = benefit.coverage_tier

    if (isUndefined(coverageTier)) {
      return null
    }

    return coverageTier
  }

  updateBenefitValues = (benefits) => {
    if (isUndefined(benefits)) {
      return []
    }

    return Object.keys(benefits).map((benefitName) => {
      return {
        name: benefitName,
        value: benefitName,
        limit: this.translateLimit(benefits[benefitName]),
        coverage_tier: this.translateCoverageTier(benefits[benefitName]),
        tiers_attributes: this.translateTiersAttributes(benefits[benefitName])
      }
    })
  }

  changesForRow = (row, initialRecord) => {
    return reduce(row, (memo, value, key) => {
      if (value !== initialRecord[key]) {
        Object.assign(memo, { [key]: value })
      }

      return memo
    }, {})
  }

  shallowRecords = (records) => {
    return records.map(this.shallowRecord)
  }

  shallowRecord = (record) => {
    const newRecord = {

      ...record,
      ...this.explodedBenefitFieldsForRow(record),
      hios_ids: this.hiosIDsForRow(record)
    }

    delete newRecord.benefits
    delete newRecord.external_ids

    return newRecord
  }

  initiateColumnBulkEdit = (column) => {
  }

  mainMenuItems = (params) => {
    const items = cloneDeep(params.defaultItems)

    if (params.column.colDef.editable) {
      items.push({
        name: 'Bulk Edit Selected',
        action: this.initiateColumnBulkEdit.bind(params.column)
      })
    }

    return items
  }

  rowMapper = (row) => {
    return {

      ...row,
      ...this.explodedBenefitFieldsForRow(row),
      hios_ids: this.hiosIDsForRow(row)
    }
  }

  hiosIDsForRow = (row) => {
    if (!row.external_ids) { return '' }
    return row.external_ids.reduce((memo, value) => {
      if (value.type === 'hios_id') {
        memo.push(value.external_id)
      }

      return memo
    }, []).join(', ')
  }

  explodedBenefitFieldsForRow = (row) => {
    return this.explodedOrderedTiersBenefitFieldsForRow(row)
  }

  explodedOrderedTiersBenefitFieldsForRow = (row) => {
    return reduce(row.benefits, (memo, value, key) => {
      return Object.assign(memo, {
        [`${key}_limit`]: value.limit,
        [`${key}_in_network`]: value.in_network,
        [`${key}_in_network_tier_2`]: value.in_network_tier_2,
        [`${key}_in_network_tier_3`]: value.in_network_tier_3,
        [`${key}_in_network_tier_4`]: value.in_network_tier_4,
        [`${key}_in_network_tier_5`]: value.in_network_tier_5,
        [`${key}_in_network_tier_6`]: value.in_network_tier_6,
        [`${key}_in_network_tier_7`]: value.in_network_tier_7,
        [`${key}_in_network_tier_8`]: value.in_network_tier_8,
        [`${key}_in_network_tier_9`]: value.in_network_tier_9,
        [`${key}_in_network_tier_10`]: value.in_network_tier_10,
        [`${key}_out_of_network`]: value.out_of_network,
        [`${key}_coverage_tier`]: value.coverage_tier,
        [`${key}_benefit`]: value.benefit,
        [`${key}_first_occurrence`]: value.first_occurrence,
        [`${key}_second_occurrence`]: value.second_occurrence
      })
    }, {})
  }

  sizeToFit = () => {
    this.gridApi.sizeColumnsToFit()
  }

  autoSizeAll = () => {
    const allColumnIds = (this.gridApi.getColumns() || []).map((column) => column.colId)

    this.gridApi.autoSizeColumns(allColumnIds)
  }

  clearEmptyBenefits = (change) => {
    const newBenefits = reduce(change.benefits, (memo, value, key) => {
      if (!isEmpty(value)) {
        Object.assign(memo, { [key]: value })
      }

      return memo
    }, {})

    return { ...change, benefits: newBenefits }
  }

  assignChangedValue = (id, fieldName, changedValue) => {
    if (this.state.pendingUpdates === false) { this.setState({ pendingUpdates: true }) }

    if (!this.state.networkOnlyUpdates && fieldName === 'network') {
      this.setState({ networkOnlyUpdates: true })
    }
    if (fieldName !== 'network') { this.setState({ networkOnlyUpdates: false }) }

    this.lastChanges.push({ id, fieldName, changedValue })
  }

  applyLastChanges = () => {
    const changes = cloneDeep(this.state.changes)

    this.lastChanges.forEach((row) => {
      const change = changes[row.id]
      const initialRecord = this.state.initialRecords[row.id]
      const fieldName = row.fieldName
      const tierType = this.tierTypeFromFieldName(fieldName)
      const changedValue = row.changedValue
      const objectFieldName = tierType.objectFieldName(row.fieldName)
      let value = row.changedValue

      if (fieldName === 'plan_years' && !isArray(changedValue) && !isEmpty(changedValue)) {
        value = changedValue.split(',').map(trim).map(Number)
      }

      if (fieldName === 'privileged_platforms' && !isArray(changedValue) && !isEmpty(changedValue)) {
        value = changedValue.split(',').map(trim).map(String)
      }

      if (changedValue !== initialRecord[fieldName]) {
        set(change, objectFieldName, value)
      } else {
        unset(change, objectFieldName)
      }

      const clearedChange = this.clearEmptyBenefits(change)

      if (isEmpty(clearedChange.benefits)) { delete clearedChange.benefits }

      changes[row.id] = clearedChange
    })

    return changes
  }

  cellValueChanged = (params) => {
    this.gridApi.autoSizeColumns([params.column.colId])
    this.assignChangedValue(params.node.id, params.colDef.field, params.newValue)
    // Disable field source priority validation for now
    // if (this.state.document) { this.validateCell(params) }
  }

  determineFieldDefinition = (params) => {
    const fieldName = params.column.colId
    const cellType = params.column.colDef.cellType
    let fieldDefinition

    if (cellType === 'metadatum') {
      fieldDefinition = this.props.metadataFields[fieldName]
    } else if (cellType === 'benefits') {
      fieldDefinition = { type: 'tiered_field' }
    }

    return fieldDefinition
  }

  // Mirrors the effective-option precedence `metadatumColumns` uses for the
  // 'select' editor/formatter (this.props.configuration[colId] overrides
  // field.options when non-empty). Clipboard coercion must resolve pasted
  // text against the SAME option list the cell actually displays/edits with,
  // or a customer-configured option set silently falls back to raw text.
  effectiveOptionsFor = (colId, fieldDefinition) => {
    if (fieldDefinition.type === 'checkbox') {
      const { options } = fieldDefinition
      return (isArray(options) && !isEmpty(options)) ? options : DEFAULT_CHECKBOX_OPTIONS
    }

    if (fieldDefinition.type !== 'select') { return fieldDefinition.options }

    const { options } = fieldDefinition
    // A plain string-array options list (no {text, value} shape) wins
    // unconditionally in metadatumColumns' 'select' case — configuration is
    // never consulted for it there, so paste coercion must not either.
    if (isArray(options) && !isEmpty(options) && !has(first(options), 'text')) {
      return options
    }

    const configured = this.props.configuration[colId]
    return (isArray(configured) && !isEmpty(configured)) ? configured : options
  }

  processCellFromClipboard = (params) => {
    const fieldDefinition = this.determineFieldDefinition(params)

    if (!isUndefined(fieldDefinition) && includes(['select', 'checkbox'], fieldDefinition.type)) {
      const effectiveOptions = this.effectiveOptionsFor(params.column.colId, fieldDefinition)
      if (isArray(effectiveOptions) && !isEmpty(effectiveOptions)) {
        const optionsHelper = new GridSelectOptionsHelper(effectiveOptions)
        const resolved = optionsHelper.resolvePastedValue(trim(params.value))
        if (!isUndefined(resolved)) { return resolved }
      }
    }

    if (isUndefined(fieldDefinition) || isUndefined(fieldDefinition.valueParser)) {
      return trim(params.value)
    } if (fieldDefinition.type === 'jsonTable') {
      return JSON.parse(params.value.replace(/##/g, '"'))
    }

    return fieldDefinition.valueParser(params.value)
  }

  processCellForClipboard = (params) => {
    const fieldDefinition = this.determineFieldDefinition(params)

    if (isUndefined(fieldDefinition) || fieldDefinition.type !== 'jsonTable') {
      return trim(params.value)
    }

    return JSON.stringify(params.value).replace(/"/g, '##')
  }

  validateCell = (params) => {
    if (params.colDef.field !== 'network') {
      this.props.higherPrioritySourceValidation({
        benefits_set_id: params.node.id,
        field: params.colDef.field,
        field_type: params.colDef.cellType,
        document_id: this.state.document
      }).then((response) => {
        if (!response.responseAttributes.valid) {
          const errorStyles = { borderColor: 'black', borderWidth: '3px', lineHeight: '1.9' }
          /* eslint-disable no-param-reassign */
          params.colDef.cellStyle = (colParams) => { return errorStyles }
          /* eslint-enable no-param-reassign */
          this.gridApi.refreshCells({ force: true, rowNodes: [params.node] })
        }
      })
    }
  }

  getRowId = (params) => {
    return String(params.data.id)
  }

  handleSave = (event) => {
    this.makeUpdate((params) => {
      this.resetInitialRecords(params)
    })
  }

  handleSaveAndReturn = (event) => {
    this.makeUpdate((params) => {
      this.setState((prevState) => {
        return { shouldRedirect: true }
      })
    })
  }

  makeUpdate = (callback) => {
    // GUARD: Prevent multiple concurrent saves
    if (this.isSaving) {
      const error = new Error('Save already in progress, ignoring duplicate request')
      ddRum.addError(error, { error_type: 'save_already_in_progress' })
      return
    }

    // GUARD: Prevent empty requests
    if (isEmpty(this.updateValues)) {
      const error = new Error('No changes to save, ignoring request')
      ddRum.addError(error, { error_type: 'no_changes_to_save' })
      return
    }

    if (this.duplicateRequest(this.updateValues)) {
      const error = new Error('Duplicate request, ignoring request')
      ddRum.addError(error, { error_type: 'duplicate_request' })
      return
    }

    this.writeRequestCache(this.updateValues)

    this.isSaving = true

    this.setState((prevState) => {
      const newSavePromise = prevState.savePromise.then(() => {
        const params = { changes: this.updateValues }
        if (this.state.withSbc) { params.with_sbc = true }
        if (this.state.withSob) { params.with_sob = true }
        if (this.state.networkOnlyUpdates) { params.networks_only_update = true }

        return this.props.createBulkUpdate(params).then((response) => {
          if (response && !response.resourceErrors) {
            callback(params)
          }
          return response
        })
      }).then((response) => {
        // Reset the guard flag after completion
        this.isSaving = false
        this.setState({ pendingUpdates: false })
        return response
      }).catch((error) => {
        // Reset the guard flag even on error
        this.isSaving = false
        this.setState({ pendingUpdates: false })
        throw error
      })

      return { savePromise: newSavePromise, changes: this.applyLastChanges() }
    })
  }

  readRequestCache = () => {
    let cache = window.localStorage.getItem(BulkEditsComponent.CACHE_KEY)

    if (!cache) { return null }

    cache = JSON.parse(cache)

    if (cache.expiresAt < Date.now()) {
      window.localStorage.removeItem(BulkEditsComponent.CACHE_KEY)
      return null
    }
    return cache.changes
  }

  writeRequestCache = (changes) => {
    const cache = {
      changes: JSON.stringify(changes),
      expiresAt: Date.now() + 1000 * 60 // 1 minute
    }
    window.localStorage.setItem(BulkEditsComponent.CACHE_KEY, JSON.stringify(cache))
  }

  duplicateRequest = (changes) => {
    const cachedChanges = this.readRequestCache()
    if (!cachedChanges) return false
    return isEqual(cachedChanges, JSON.stringify(changes))
  }

  handleChange = (name, value) => {
    this.setState((prevState) => {
      return { [name]: value }
    })
  }

  filterWithSBC = () => {
    this.setState((prevState) => {
      return { withSbc: !prevState.withSbc }
    })
  }

  filterWithSOB = () => {
    this.setState((prevState) => {
      return { withSob: !prevState.withSob }
    })
  }

  applyPresetFilter = (event) => {
    this.presetFilter = event.target.value
    const newColumns = this.columns

    this.setState((prevState) => ({
      columns: newColumns,
      gridKey: (prevState.gridKey || 0) + 1
    }))
  }

  resetInitialRecords = (params) => {
    this.setState((prevState) => {
      const newInitialRecords = prevState.initialRecords
      const changes = prevState.changes

      params.changes.forEach((change) => {
        forEach(change.metadatum_attributes, (value, key) => {
          newInitialRecords[change.benefits_set_id][key] = value
          delete changes[change.benefits_set_id][key]
        })

        forEach(change.benefits_attributes, (benefit) => {
          if (benefit.limit) {
            newInitialRecords[change.benefits_set_id][`${benefit.name}_limit`] = benefit.limit
            delete changes[change.benefits_set_id].benefits[benefit.name].limit
          }
          if (benefit.coverage_tier) {
            newInitialRecords[change.benefits_set_id][`${benefit.name}_coverage_tier`] = benefit.coverage_tier
            delete changes[change.benefits_set_id].benefits[benefit.name].coverage_tier
          }
          forEach(benefit.tiers_attributes, (tier) => {
            const tierName = tier.option.toLowerCase().replace(/\s+/g, '_')
            newInitialRecords[change.benefits_set_id][`${benefit.name}_${tierName}`] = tier.value
            delete changes[change.benefits_set_id].benefits[benefit.name][tierName]
          })
        })
        forEach(changes[change.benefits_set_id].benefits, (value, key) => {
          if (isEmpty(value)) {
            delete changes[change.benefits_set_id].benefits[key]
          }
        })

        if (isEmpty(changes[change.benefits_set_id].benefits)) {
          delete changes[change.benefits_set_id].benefits
        }
      })
      return { changes, initialRecords: newInitialRecords }
    })
  }

  tierTypeFromLabel = (name) => {
    const {
      fieldGroups
    } = this.props

    if (isUndefined(fieldGroups)) { return networkTierType }
    const index = fieldGroups.Benefits.benefits.fields.findIndex((group) => group.fields.find((b) => b.label === name))
    if (index >= 0 && !isUndefined(fieldGroups.Benefits.benefits.fields[index].tier)) {
      return fieldGroups.Benefits.benefits.fields[index].tier
    }

    return networkTierType
  }

  tierTypeFromFieldName = (fieldName) => {
    const {
      fieldGroups
    } = this.props

    if (isUndefined(fieldGroups)) { return networkTierType }

    const regex = /(.*?)(_second_occurrence|_first_occurrence|_limit|_benefit)/
    let name = ''
    const matches = fieldName.match(regex)

    if (!isNil(matches) && matches.length > 0) {
      name = matches[1]
    }

    const index = fieldGroups.Benefits.benefits.fields.findIndex((group) => group.fields.find((b) => b.name === name))
    if (index >= 0 && !isUndefined(fieldGroups.Benefits.benefits.fields[index].tier)) {
      return fieldGroups.Benefits.benefits.fields[index].tier
    }

    return networkTierType
  }

  titleCase(str) {
    return str.replace(/^_*(.)|_+(.)/g, (s, c, d) => { return c ? c.toUpperCase() : ` ${d.toUpperCase()}` })
  }

  sortedFields(fieldsArray, orderedArray) {
    const order = orderedArray.slice().reverse()

    return fieldsArray
      .slice()
      .sort((a, b) => { return order.indexOf(a.field) - order.indexOf(b.field) })
      .reverse()
  }

  excludedByPreset(field) {
    return !this.props.defaultFields[this.presetFilter || 'default'].values.includes(field)
  }

  pinned(field) {
    return this.props.defaultFields[this.presetFilter || 'default'].pinned.includes(field)
  }

  render() {
    if (this.state.shouldRedirect) {
      return <Redirect replace to={this.props.redirectUrl} />
    }

    return (
      <div>
        {this.documentLookup}
        {this.sbcCheckbox}
        {this.sobCheckbox}
        {this.presetDropdown}
        <div className="flash-message-contains">
          <p className={this.savingMsgClass}>{this.savingMsg}</p>
        </div>
        <div className="ag-theme-balham data-grid">
          <AgGridReact
            key={this.state.gridKey || 0}
            theme={agGridTheme}
            columnDefs={this.state.columns}
            rowData={this.filteredRecords}
            onGridReady={this.onGridReady}
            onCellValueChanged={this.cellValueChanged}
            processCellForClipboard={this.processCellForClipboard}
            processCellFromClipboard={this.processCellFromClipboard}
            onCellEditingStopped={this.cellEditingStopped}
            getMainMenuItems={this.mainMenuItems}
            getRowId={this.getRowId}
            context={{ initialData: this.state.initialRecords }}
            suppressAggFuncInHeader
            cellSelection
            sideBar={{
              toolPanels: [{
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true
                }
              }]
            }}
          />
        </div>
        <BulkEditsErrorsComponent
          bulkUpdate={this.props.bulkUpdate}
          explodedBenefitFieldsForRow={this.explodedBenefitFieldsForRow}
        />
        <fieldset>
          <button
            className="pure-button pure-input-1 pure-button-primary"
            title="Save and Continue"
            id="save_and_continue"
            onClick={this.handleSave}
            disabled={this.savingDisabled}
          >
            Save and Continue
          </button>

          <button
            className="pure-button pure-input-1 pure-button-primary"
            title="Save and Back"
            id="save_and_back"
            onClick={this.handleSaveAndReturn}
            disabled={this.savingDisabled}
          >
            Save and Back
          </button>
        </fieldset>
      </div>
    )
  }
}
